import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useProductAddToCart } from "../../../../../cart/hooks/useProductAddToCart";
import type { TransformedProduct } from "../../../../types/productApiTypes";
import QuantityControl from "../../../../../cart/components/common/QuantityControl";
import Icon from "../../../../../../../common/components/Icon/Icon";
import { useOpenStockNotificationModal } from "../../../../../stockNotification/hooks/useOpenStockNotificationModal";

interface ProductActionsProps {
  product: TransformedProduct;
  selectedVariation: TransformedProduct | null;
  selectedAttributes: { [key: string]: string };
  showDetailsLink?: boolean;
}

const ProductActions: React.FC<ProductActionsProps> = ({
  product,
  selectedVariation,
  selectedAttributes,
  showDetailsLink = true,
}) => {
  const { t } = useTranslation();
  const { handleAddToCart, isAddingToCart } = useProductAddToCart();
  const openStockModal = useOpenStockNotificationModal();

  // Determine the correct data source (variation or parent product)
  // Note: For variable products without a selection, displayData is the parent.
  const displayData = selectedVariation || product;

  // --- Sold Individually Logic ---
  const isSoldIndividually = displayData.soldIndividually;

  // Derive min/max quantities. The QuantityControl component will handle capping the max at 20.
  const minQuantity = displayData.minStock || 1;
  // If sold individually, force max to 1, otherwise use maxStock (QuantityControl caps at 20)
  const maxQuantity = isSoldIndividually
    ? 1
    : Math.min(displayData.maxStock ?? 99, 20);

  // Local state for the quantity input
  const [quantity, setQuantity] = useState(minQuantity);

  // Effect to reset/clamp quantity when the selected variation or product changes
  useEffect(() => {
    const currentIsSoldIndividually =
      selectedVariation?.soldIndividually || product.soldIndividually;

    const newMin = (selectedVariation?.minStock || product.minStock) ?? 1;
    // We only need the backend's max here. The component will clamp it.
    const newMax = currentIsSoldIndividually
      ? 1
      : ((selectedVariation?.maxStock || product.maxStock) ?? 99);

    // Clamp the current quantity within the new bounds, or reset to min
    setQuantity((currentQty) => Math.max(newMin, Math.min(currentQty, newMax)));
  }, [selectedVariation, product]);

  // --- Handlers for Quantity Input ---
  const handleQuantityChange = (newValue: number) => {
    setQuantity(newValue);
  };

  // For external products, we only show a single button linking to the external URL
  if (product.type === "external") {
    return (
      <div className="product-info-details__actions">
        <a
          href={product.addToCartUrl}
          className="product-info-details__external-btn"
          target="_blank"
          rel="noopener noreferrer"
          title={product.addToCartText}
        >
          <span>{t("productInfoDetails.buyProduct")}</span>
          <Icon iconName="external-link" className="icon--stroke-thick" />
        </a>
      </div>
    );
  }

  // --- START: Validation Logic ---

  // 1. Check Purchasability (Gatekeeper 1)
  // If false, product has no price or is disabled via plugin.
  // We check displayData to handle cases where a specific variation might be disabled.
  const isPurchasable = displayData.purchasable;

  // 2. Check Stock & Backorder (Gatekeeper 2)
  // stockStatus 'outofstock' means 0 inventory.
  // onBackorder means inventory is 0 BUT backorders are allowed, so buying is okay.
  const isOutOfStock = displayData.stockStatus === "outofstock";
  const isOnBackorder = displayData.onBackorder;

  // 3. Variable Product Specific Checks
  let warningMessage: string | null = null;
  let isSelectionIncomplete = false;
  let isCombinationInvalid = false;

  if (product.isVariable) {
    const requiredAttributeCount = product.enrichedAttributes.filter(
      (attr) => attr.hasVariations,
    ).length;
    const selectedAttributeCount = Object.keys(selectedAttributes).length;

    isSelectionIncomplete = selectedAttributeCount < requiredAttributeCount;
    // If selection is complete but we have no variation object, it's invalid
    isCombinationInvalid = !isSelectionIncomplete && !selectedVariation;

    if (isSelectionIncomplete) {
      warningMessage = t("productInfoDetails.selectAttributesMessage");
    } else if (isCombinationInvalid) {
      warningMessage = t("productInfoDetails.invalidCombinationMessage");
    }
  }

  // 4. Check for General Purchasability
  // This runs if there are no specific selection errors (or if it's a simple product).
  // It catches cases where a Simple Product is catalog-only, or a specific Variation has no price.
  // Note: We skip this warning if OutOfStock, because we show "Notify Me" instead of an error message.
  if (!warningMessage && !isPurchasable && !isOutOfStock) {
    warningMessage = t(
      "productInfoDetails.unavailableMessage",
      "This product is currently unavailable.",
    );
  }

  // --- Combined Disable Logic (For Add to Cart Button) ---
  const isAddToCartDisabled =
    isAddingToCart ||
    !isPurchasable ||
    (isOutOfStock && !isOnBackorder) ||
    isSelectionIncomplete ||
    isCombinationInvalid;

  const onAddToCartClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    let productIdToAdd = product.id;
    // Prepare the variation array for the API
    let variationData: { attribute: string; value: string }[] | undefined;

    if (product.isVariable) {
      if (!selectedVariation) {
        return; // Safeguard
      }
      productIdToAdd = selectedVariation.id;

      // --- ROBUSTNESS UPDATE: Format attributes for API ---
      // Maps { 'pa_color': 'blue' } -> [{ attribute: 'pa_color', value: 'blue' }]
      variationData = Object.entries(selectedAttributes).map(
        ([attribute, value]) => ({
          attribute,
          value,
        }),
      );
    }

    handleAddToCart(e, {
      productId: productIdToAdd,
      quantity: quantity,
      variation: variationData, // Pass explicit attributes
    });
  };

  // --- Notify Me Logic ---
  const handleNotifyMe = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    // Determine IDs.
    // If it's a variable product, we need the specific variation ID for tracking.
    const variationId = selectedVariation ? selectedVariation.id : undefined;

    // Use a clearer name for the modal title if a variation is selected
    const displayName = selectedVariation
      ? `${product.name} - ${selectedVariation.name}`
      : product.name;

    openStockModal({
      productId: product.id,
      variationId,
      productName: displayName,
    });
  };

  return (
    <div className="product-info-details__actions">
      {warningMessage && (
        <div className="product-info-details__actions-warning">
          {warningMessage}
        </div>
      )}

      <div className="product-info-details__actions-main">
        {/*
           LOGIC SWITCH for Rendering Buttons:
           1. IF Out Of Stock & Not Backorder (and selections are valid) -> Show "Notify Me" button.
              (This replaces the Add to Cart button and Quantity selector completely).
              This takes precedence over standard purchase actions but only if selection is complete.
           2. ELSE (In Stock, Backorder, or Incomplete Selection) -> Show Quantity + Add to Cart button (Standard flow).
              (If selection is incomplete, the button will be disabled via isAddToCartDisabled).
        */}
        {isOutOfStock && !isOnBackorder && !isSelectionIncomplete ? (
          <div className="product-info-details__notify-wrapper">
            <button
              className="product-info-details__notify-btn"
              onClick={handleNotifyMe}
            >
              <Icon iconName="bell" className="icon--stroke-thickest" />
              <span>{t("productInfoDetails.notifyMe", "Notify me")}</span>
            </button>
          </div>
        ) : (
          <div className="product-info-details__purchase-actions">
            <QuantityControl
              value={quantity}
              onChange={handleQuantityChange}
              min={minQuantity}
              max={maxQuantity} // Pass the backend's max quantity
              // Disable quantity control if sold individually or general cart add is disabled
              disabled={isAddToCartDisabled || isSoldIndividually}
              ariaLabel={t("productInfoDetails.quantityLabel", {
                productName: product.name,
              })}
              className="product-info-details__quantity-selector"
            />
            <button
              className="product-info-details__add-to-cart-btn"
              onClick={onAddToCartClick}
              disabled={isAddToCartDisabled}
            >
              {isAddingToCart ? (
                t("productInfoDetails.addingToCart")
              ) : !isPurchasable ? (
                // State 1: Not Purchasable (e.g. No Price / Catalog Mode)
                <>
                  <span>
                    {t("productInfoDetails.unavailable", "Unavailable")}
                  </span>
                  <Icon iconName="info-outlined" className="icon--filled" />
                </>
              ) : isOutOfStock && !isOnBackorder ? (
                // State 2: Out of Stock (This branch is largely unreachable due to the switch above,
                // but kept as a defensive fallback for edge cases like incomplete selection on OOS variable product)
                <>
                  <span>{t("productInfoDetails.outOfStock")}</span>
                  <Icon iconName="not-allowed" className="icon--filled" />
                </>
              ) : isOnBackorder ? (
                // State 3: Backorder (Purchasable)
                <>
                  <span>{t("productInfoDetails.backorder", "Backorder")}</span>
                  <Icon
                    iconName="shopping-cart-filled"
                    className="icon--filled"
                  />
                </>
              ) : (
                // State 4: Standard Add to Cart
                <>
                  <span>{t("productInfoDetails.addToCartLong")}</span>
                  <Icon
                    iconName="shopping-cart-filled"
                    className="icon--filled"
                  />
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {showDetailsLink && (
        <a
          href={product.permalink}
          className="product-info-details__view-details-btn"
        >
          {t("productInfoDetails.viewFullDetails")}
        </a>
      )}
    </div>
  );
};

export default ProductActions;
